import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Format message for WhatsApp
    const whatsappMessage = `*New Contact Form Submission*\n\n` +
      `*Name:* ${formData.name}\n` +
      `*Email:* ${formData.email}\n` +
      `*Message:*\n${formData.message}\n\n` +
      `_Submitted from Fiction Goon Films Website_`;

    // Encode message for URL
    const encodedMessage = encodeURIComponent(whatsappMessage);

    // Phone number: 9599082331 (WhatsApp format: 91+number)
    const phoneNumber = '919599082331';

    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

    // Show success message
    alert('Thank you for your message! Opening WhatsApp to send your message...');

    // Reset form
    setFormData({ name: '', email: '', message: '' });

    console.log('Form submitted:', formData);
  };

  const contactInfo = [
    {
      icon: '📞',
      type: 'phone',
      items: [
        { label: 'Phone', value: '+91-9599082331', link: 'tel:+91-9599082331' },
      ],
    },
    {
      icon: '✉️',
      type: 'email',
      items: [
        { label: 'Email', value: 'ceofgf@gmail.com', link: 'mailto:ceofgf@gmail.com' },
      ],
    },
    {
      icon: '📍',
      type: 'location',
      items: [
        {
          label: 'Address',
          value: 'Fiction Goon Films Private Limited,\nC 173, Block C, Sector 122, Noida,\nUttar Pradesh 201316',
          link: 'https://www.google.com/maps/search/?api=1&query=Fiction+Goon+Films+Private+Limited+C+173+Block+C+Sector+122+Noida+Uttar+Pradesh+201316'
        },
      ],
    },
  ];

  return (
    <section id="contact" className="contact" ref={ref}>
      <div className="contact-background">
        <div className="contact-gradient-orb orb-1"></div>
        <div className="contact-gradient-orb orb-2"></div>
      </div>

      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">
            Let's Build Something Extraordinary Together
          </p>
        </motion.div>

        <div className="contact-wrapper">
          {/* Contact Information Side */}
          <motion.div
            className="contact-info-section"
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="contact-info-card">
              <h3 className="contact-info-title">Contact Information</h3>
              <p className="contact-info-subtitle">Reach out to us through any of these channels</p>

              <div className="contact-info-list">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    className="contact-info-item"
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                  >
                    <div className="contact-icon-wrapper">
                      <span className="contact-icon">{info.icon}</span>
                    </div>
                    <div className="contact-details">
                      {info.items.map((item, idx) => (
                        <div key={idx} className="contact-detail-item">
                          {item.link ? (
                            <a
                              href={item.link}
                              target={item.link.startsWith('http') ? '_blank' : '_self'}
                              rel={item.link.startsWith('http') ? 'noopener noreferrer' : ''}
                              className="contact-link"
                            >
                              {item.value.split('\n').map((line, i) => (
                                <React.Fragment key={i}>
                                  {line}
                                  {i < item.value.split('\n').length - 1 && <br />}
                                </React.Fragment>
                              ))}
                            </a>
                          ) : (
                            <span className="contact-text">
                              {item.value.split('\n').map((line, i) => (
                                <React.Fragment key={i}>
                                  {line}
                                  {i < item.value.split('\n').length - 1 && <br />}
                                </React.Fragment>
                              ))}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Map Preview */}
            <motion.div
              className="map-preview"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="map-container">
                <iframe
                  src="https://maps.google.com/maps?q=Fiction+Goon+Films+Private+Limited+C+173+Block+C+Sector+122+Noida+Uttar+Pradesh+201316&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0, borderRadius: '16px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="PrTechZone Location"
                ></iframe>
              </div>
            </motion.div>
          </motion.div>

          {/* Contact Form Side */}
          <motion.div
            className="contact-form-section"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="contact-form-card">
              <h3 className="contact-form-title">Send Us a Message</h3>
              <p className="contact-form-subtitle">Fill out the form below and we'll get back to you</p>

              <form className="contact-form" onSubmit={handleSubmit}>
                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </motion.div>

                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </motion.div>

                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                >
                  <textarea
                    name="message"
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="form-input form-textarea"
                  />
                </motion.div>

                <motion.button
                  type="submit"
                  className="btn btn-primary btn-submit"
                  whileHover={{ scale: 1.02, boxShadow: "0 10px 30px rgba(124, 58, 237, 0.4)" }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  Send Message
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
